"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { DreamLogo } from "@portal/app/_components/ui-elements/DreamLogo";

interface BrandHeaderProps {
  providerName: string;
  providerLogoUrl?: string;
}

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

/**
 * Renders "Dreambound | <provider>" as a row when it fits the container,
 * and stacks vertically when the row would overflow (e.g. long school names
 * on narrow viewports). A hidden measurement twin keeps the natural row
 * width stable across re-renders so the layout doesn't oscillate.
 */
export const BrandHeader = ({ providerName, providerLogoUrl }: BrandHeaderProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [isStacked, setIsStacked] = useState(false);

  useIsomorphicLayoutEffect(() => {
    const container = containerRef.current;
    const measureEl = measureRef.current;
    if (!container || !measureEl || typeof ResizeObserver === "undefined") return;

    const measure = () => {
      setIsStacked(measureEl.scrollWidth > container.clientWidth);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, [providerName, providerLogoUrl]);

  const renderProvider = () =>
    providerLogoUrl ? (
      <Box
        component="img"
        src={providerLogoUrl}
        alt={`${providerName} logo`}
        sx={{ height: 24, maxWidth: 160, objectFit: "contain" }}
      />
    ) : (
      <Typography
        component="span"
        sx={{
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          textAlign: "center",
        }}
      >
        {providerName}
      </Typography>
    );

  return (
    <Box
      ref={containerRef}
      data-testid="success-brand-header"
      sx={{
        position: "relative",
        width: "100%",
        display: "flex",
        flexDirection: isStacked ? "column" : "row",
        alignItems: "center",
        justifyContent: "center",
        gap: isStacked ? 1 : 1.5,
        mb: 3,
      }}
    >
      <DreamLogo height={24} />
      {!isStacked && (
        <Box
          aria-hidden="true"
          sx={{ width: "1px", height: 20, bgcolor: "divider", flexShrink: 0 }}
        />
      )}
      {renderProvider()}

      {/* Hidden measurement twin: always horizontal, never wraps. Drives the
          stack/row decision so the visible layout doesn't oscillate when the
          stacked column reports a different intrinsic width than the row. */}
      <Box
        ref={measureRef}
        aria-hidden="true"
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          visibility: "hidden",
          pointerEvents: "none",
          display: "inline-flex",
          alignItems: "center",
          gap: 1.5,
          whiteSpace: "nowrap",
        }}
      >
        <DreamLogo height={24} />
        <Box sx={{ width: "1px", height: 20 }} />
        {renderProvider()}
      </Box>
    </Box>
  );
};
